import { IDataPage, IItem, IItemFilters, PaginatedDataSet, ItemsState, DataPageOptions } from "Interfaces";
import * as ItemsApi from "Data/API/Items";
import { createAppSlice } from "Data/Redux/Helpers";
import { createSelector, PayloadAction } from "@reduxjs/toolkit";
import { filterPaginatedDataById, getPaginatedDataOptions, handleDataPage } from "Data/Utils/Redux";
import { createIdentitySelector } from "@clintonelec/typescriptutils";
import type { AppState } from "Data/Redux/Reducers";

const initialState: ItemsState = {
	data: [],
	lastPage: -1,
	options: {
		"page[size]": 200,
		"page[number]": 1
	}
};

// #region Reducers

export const setItemsOptionsReducer = (state: PaginatedDataSet<IItem>, action: PayloadAction<DataPageOptions>) => {
	state.options = action?.payload;
	state.data = [];
	state.lastPage = -1;
};

// #endregion

// #region Selectors

const getFetchable = (state: ItemsState) => {
	const { lastPage, options } = state;
	const hasLookupFilters = Object.keys(options).length > 2;
	const nextPageExists = lastPage === -1 || Number(options["page[number]"] ?? -1) <= lastPage;

	return hasLookupFilters && nextPageExists;
};

const getItems = (state: ItemsState) => {
	const { options } = state;
	const hasLookupFilters = Object.keys(options).length > 2;

	if (!hasLookupFilters) {
		return [];
	}

	return state?.data;
};

const getFiltered = (filters: IItemFilters, items: IItem[]) => {
	return items.filter((item) => {
		const { serialNumber, scannedAt, order } = item;
		const {
			scannedAfter, scannedBefore, serialDateUpper, serialDateLower, serialRangeLower,
			serialRangeUpper, serialNumber: serialNumberFilter, orderNumber: orderNumberFilter,
			selectedPartNumbers: partNumbers
		} = filters;

		const hasOrderFilter = orderNumberFilter
			? order?.orderNumber.toLowerCase().includes(orderNumberFilter.toString().toLowerCase())
			: true;

		const hasSerialNumberFilter = serialNumberFilter
			? serialNumber?.toLowerCase().includes(serialNumberFilter.toString().toLowerCase())
			: true;

		const matchesPartNumberFilter = partNumbers.length > 0
			? partNumbers.some((partNumber) => partNumber.toLowerCase().includes(item.partNumber?.toLowerCase() ?? ""))
			: true;

		// serial range
		const serialRange = +serialNumber.substring(serialNumber.length - 4);
		const lowerRange = serialRangeLower ? +serialRangeLower : serialRange;
		const upperRange = serialRangeUpper ? +serialRangeUpper : serialRange;
		// scan date
		const scannedAtDate = new Date(scannedAt);
		const isScannedAfter = scannedAfter ? scannedAtDate >= new Date(scannedAfter) : true;
		const isScannedBefore = scannedBefore ? scannedAtDate <= new Date(scannedBefore) : true;
		const isInScanDateRange = isScannedAfter && isScannedBefore;
		// serial date
		const serialDate = new Date(serialNumber.substring(0, 6).replace(/(.{2})/g, "$1/"));
		const lowerDate = serialDateLower ? new Date(serialDateLower) : serialDate;
		const upperDate = serialDateUpper ? new Date(serialDateUpper) : serialDate;

		return serialDate >= lowerDate && serialDate <= upperDate
			&& serialRange >= lowerRange && serialRange <= upperRange && isInScanDateRange && hasOrderFilter
			&& hasSerialNumberFilter && matchesPartNumberFilter;
	});
};

const getFilteredItems = createSelector(
	createIdentitySelector<ItemsState, IItemFilters>(),
	(state: ItemsState, filters: IItemFilters, items: IItem[]) => items,
	getFiltered
);

const getItemById = createSelector(
	(state: ItemsState) => state,
	createIdentitySelector<ItemsState, string>(),
	filterPaginatedDataById<IItem>
);

const getProgress = (state: ItemsState) => {
	const { lastPage, options } = state;

	return Number(options["page[number]"]) / lastPage * 100;
};

// #endregion

const itemsSlice = createAppSlice({
	name: "items",
	initialState,
	reducers: (builder) => ({
		setItemsOptionsAction: builder.reducer(setItemsOptionsReducer),
		fetchNextItemsPageAction: builder.asyncThunk(async (payload: void, thunkApi): Promise<IDataPage<IItem>> => {
			const { items, users: { token } } = thunkApi.getState() as AppState;

			return ItemsApi.getItems(token, getPaginatedDataOptions(items));
		}, {
			fulfilled: handleDataPage
		})
	}),
	selectors: {
		selectItems: getItems,
		selectItemsFetchable: getFetchable,
		selectItemById: getItemById,
		selectItemsOptions: getPaginatedDataOptions,
		selectItemsProgress: getProgress,
		selectFilteredItems: getFilteredItems
	}
});

export default itemsSlice;

export const { setItemsOptionsAction, fetchNextItemsPageAction } = itemsSlice.actions;

export const {
	selectItems, selectItemsFetchable, selectItemById, selectItemsOptions, selectItemsProgress,
	selectFilteredItems
} = itemsSlice.selectors;
